import Vue from "vue";
import { mapGetters, mapState } from "vuex";

import actionBarComponent from "@/components/child/actionBar/actionBar.vue";
import advancedfilepicker from "@/components/child/form/advancedfilepicker.vue";
import cepFieldComponent from "@/components/child/form/cepfield.vue";
import checkboxComponent from "@/components/child/form/checkbox.vue";
import cnpjComponent from "@/components/child/form/cnpj.vue";
import comboComponent from "@/components/child/form/combo.vue";
import dateTimePickerComponent from "@/components/child/form/datetimepicker.vue";
import fieldsetComponent from "@/components/child/form/fieldset.vue";
import textareaComponent from "@/components/child/form/textarea.vue";
import logEntidadeComponent from "@/components/child/logEntidade/logEntidade.vue";
import { Component, Watch } from "@/decorators";
import CepModel from "@/models/cepModel";
import AreaAtuacao from "@/models/enum/areaAtuacao";
import EnumExtensions from "@/models/enum/extensions/enumExtensions";
import TipoCr from "@/models/enum/tipoCr";
import TiposFranquia from "@/models/enum/tiposFranquia";
import Ufs from "@/models/enum/ufs";
import FranquiaModel from "@/models/franquiaModel";
import UsuarioModel from "@/models/usuarioModel";
import CidadeService from "@/services/external/cidadeService";
import { default as franquiaService } from "@/services/franquiaService";
import RedeService from "@/services/rede/redeService";
import { AppState, Getters, SessionActions } from "@/store/store";

import "../crud.scss";

@Component({
    components: {
        comboComponent,
        fieldsetComponent,
        cnpjComponent,
        cepFieldComponent,
        checkboxComponent,
        dateTimePickerComponent,
        actionBarComponent,
        textareaComponent,
        logEntidadeComponent,
        advancedfilepicker,
    },
    computed: mapState({
        ...mapState<AppState>({
            usuarioLogado: state => state.session.usuarioLogado,
        }),
        ...mapGetters(["HAS_PERMISSAO_PERFIL"] as Getters),
    }),
})
export default class FranquiaEditComponent extends Vue {
    // State computed props
    usuarioLogado: UsuarioModel;
    HAS_PERMISSAO_PERFIL: (isRede: boolean, isFranqueador: boolean, isSuporte: boolean) => Promise<boolean>;
    private cidadeService = new CidadeService();
    private redeService = new RedeService();

    model: FranquiaModel = new FranquiaModel();
    modelId = 0;
    isAdmin = false;
    permiteExcluirDados = false;
    redePossuiDotz = false;
    logoFile: File = null;

    dadosCep: CepModel = new CepModel();
    ufOptions: Array<Object> = EnumExtensions.getNamesAndValuesOrderedByNames(Ufs);
    areaAtuacaoOptions: Array<Object> = EnumExtensions.getNamesAndValuesOrderedByNames(AreaAtuacao);
    tipoCrOptions: Array<Object> = EnumExtensions.getNamesAndValuesOrderedByNames(TipoCr);
    tipoFranquiaOptions: Array<Object> = EnumExtensions.getNamesAndValuesOrderedByNames(TiposFranquia);
    cidadesOptions: Array<Object> = [];
    cidadesEntregaOptions: Array<Object> = [];
    redeOptions: Array<Object> = [];
    matrizOptions: Array<Object> = [];

    naturezaOperacaoOptions: Array<Object> = [
        { text: "Não Informado", value: 0 },
        { text: "1 Tributação No Municipio", value: 1 },
        { text: "2 Tributação Fora Do Municipio", value: 2 },
        { text: "3 Isenção", value: 3 },
        { text: "4 Imune", value: 4 },
        { text: "5 Exigibilidade Suspensa Decisao Judicial", value: 5 },
        { text: "6 Exigibilidade Suspensa Procedimento Administrativo", value: 6 },
    ];

    private async load() {
        this.model = new FranquiaModel();
        this.model.areaAtuacao = 0;
        this.logoFile = null;

        if (this.modelId > 0) {
            try {
                const data = await franquiaService.get(this.modelId);

                this.model.updateFrom(data);
                await this.onLoadCidades(this.model.estadoId);

                const usuario = this["usuarioLogado"] as UsuarioModel;
                this.permiteExcluirDados =
                    (await this.HAS_PERMISSAO_PERFIL(false, false, false)) &&
                    this.model.id > 0 &&
                    this.model.id != usuario.franquiaId;
            } catch {
                this.$router.back();
            }
        }
    }

    private async save() {
        try {
            const isValid = await this.$validator.validateAll();
            if (isValid) {
                if (!this.redePossuiDotz && this.model.utilizaDotz) {
                    this.model.utilizaDotz = false;
                }

                const sucesso = await franquiaService[!this.model.id ? "insertWithLogo" : "updateWithLogo"](
                    this.model,
                    this.logoFile,
                )
                    .withLoading()
                    .resolveWithoutJSON();

                if (sucesso) {
                    await this.$showInclusaoUpdate(this.model.id);
                    await this.$store.dispatch(SessionActions.RELOAD_USUARIO_LOGADO);
                    this.$router.back();
                }
            } else {
                this.$focusErrorField();
            }
        } catch {}
    }

    private onLogoFileSelected(file: File) {
        this.logoFile = file;
    }

    private cancel() {
        this.$router.back();
    }

    private async onReplicar() {
        try {
            const franquiaBase = await franquiaService
                .getFranquiaBaseReplicar(this.model.id)
                .withLoading()
                .then(response => response.json());

            const confirmar = await this.$showQuestion(
                this.$t("__.ts.confirmarReplicarFranquia"),
                this.$t("__.ts.msgConfirmarReplicarFranquia", {
                    franquiaOrigem: franquiaBase.nomeFantasia,
                    franquiaDestino: this.model.nomeFantasia,
                }),
            );

            if (!confirmar) {
                return;
            }

            const sucesso = await franquiaService.replicar(this.model.id, 1).withLoading().resolveWithoutJSON();
            if (sucesso) {
                await this.$showSuccess(this.$t("__.ts.replicaFranquia"), this.$t("__.ts.replicasBaseSalvas"));
                this.$router.back();
            }
        } catch {}
    }

    private async excluirDados() {
        try {
            const confirmar = await this.$showQuestion(this.$t("__.ts.atencao"), this.$t("__.ts.msgExcluirDados"));
            if (confirmar) {
                const sucesso = await franquiaService
                    .excluirDadosFranquia(this.model.id)
                    .withLoading()
                    .resolveWithoutJSON();
                if (sucesso) {
                    await this.$showExclusao();
                    this.$router.back();
                } else {
                    await this.$showError(this.$t("__.ts.erro"), this.$t("__.ts.msgFalhaExclusao"));
                }
            }
        } catch {}
    }

    private async onCepChanged(data: CepModel) {
        this.dadosCep = data;
        this.model.bairro = data.bairro;
        this.model.complemento = data.complemento;
        this.model.logradouro = data.endereco;
        this.model.estadoId = data.uf;
        await this.onLoadCidades(this.model.estadoId);
        this.model.cidadeId = data.cidadeId;
    }

    private async loadRede() {
        try {
            if (this.isAdmin) {
                const data = await this.redeService.getComboRede().then(r => r.json());
                for (let i = 0; data.length > i; i++) {
                    this.redeOptions.push({ value: data[i].id, text: data[i].rede });
                }
            }
        } catch {}
    }

    private async loadMatriz() {
        try {
            const franquias = await franquiaService.comboCached(this.usuarioLogado.franquiaId);
            this.matrizOptions = franquias.map(item => ({
                value: item.id,
                text: `${item.nomeFantasia} - [${item.cnpj}]`,
            }));
        } catch {}
    }

    private onCepInvalid() {
        this.$showInfo(this.$t("__.ts.pesqCEP"), this.$t("__.ts.pesqNaoEncResul"));
    }

    private async onLoadCidades(estadoId: number) {
        try {
            this.cidadesOptions = [];
            if (estadoId) {
                this.cidadesOptions = await this.cidadeService.get(estadoId).withLoading();
            }
        } catch {}
    }

    private async onShowLogFranquia(open: (slot: number, title: string, size: string) => {}) {
        open(2, this.$t("__.ts.histAlterFranquia") as string, "lg-modal");
    }

    //@ts-ignore
    @Watch("model.redeId")
    private async onRedeIdChanged(redeId: number) {
        if (redeId > 0) {
            try {
                this.redePossuiDotz = await this.redeService
                    .getRedeUtilizaDots(redeId)
                    .then(response => response.json() as Promise<boolean>);
            } catch {}
        }
    }

    private async mounted() {
        this.isAdmin = await this.HAS_PERMISSAO_PERFIL(true, false, false);

        Promise.all([this.loadRede(), this.loadMatriz()])
            .withLoading()
            .then(() => {
                if (this.$route.params.id) {
                    this.modelId = +this.$route.params.id;

                    this.load();
                }
            });
    }
}
